import React, { useCallback } from 'react';
import { useLingui } from '@lingui/react';

import { LOCATION_DEFAULT_PLACEHOLDER_VARIANT } from '@rover/constants/src/location/location.constants';
import Map from '@rover/icons/svg/map.svg';
import { Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import LocationInput from '@rover/react-lib/src/containers/LocationInputContainer';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import { SearchDesktopUpdateLocation } from '../analyticsEvents';

type LocationChangeData = {
  value?: string | null;
  lat?: string | null;
  lng?: string | null;
  locationAccuracy?: string | null;
  locationType?: string | null;
  rawLocationTypes?: string[] | null;
};

type LocationSectionProps = {
  location?: string;
  updateFiltersAndFireSearchOnChange: (filters: Partial<SearchFilters>) => void;
};

const LocationSection = ({
  location,
  updateFiltersAndFireSearchOnChange,
}: LocationSectionProps): JSX.Element => {
  const theme = useTheme();
  const { i18n } = useLingui();

  const handleChange = useCallback(
    (data: LocationChangeData | null): void => {
      emitAnalyticsEvent(new SearchDesktopUpdateLocation());

      updateFiltersAndFireSearchOnChange({
        centerlat: data?.lat ? parseFloat(data.lat) : undefined,
        centerlng: data?.lng ? parseFloat(data.lng) : undefined,
        location: data?.value || undefined,
        locationAccuracy: data?.locationAccuracy || undefined,
        locationType: data?.locationType || undefined,
        maxlat: undefined,
        maxlng: undefined,
        minlat: undefined,
        minlng: undefined,
        rawLocationTypes: data?.rawLocationTypes || undefined,
        zoomlevel: undefined,
      });
    },
    [updateFiltersAndFireSearchOnChange]
  );

  return (
    <Flex
      position="relative"
      flex="1"
      height="100%"
      sx={{
        '& input': {
          border: 'none',
        },
      }}
      data-testid="location-section"
    >
      <LocationInput
        channel="ssr-search"
        enableLatLng
        inputId="location-input-simplified-filters"
        inputValue={location}
        onChange={handleChange}
        placeholder={i18n._(LOCATION_DEFAULT_PLACEHOLDER_VARIANT)}
        placeholderColor={theme.textColor.tertiary}
        validationType="inline"
        renderIcon={() => <Map width={24} height={24} fill={theme.colors.brand.active_brand} />}
        inputHeight="56px"
        enableTwoLineSuggestions
      />
    </Flex>
  );
};

export default LocationSection;
